/**
 * « Le cahier de la semaine », côté parent — lot P5.
 *
 * En fin de semaine l'enfant photographie les pages de son cahier de classe,
 * matière par matière ; un modèle les lit. Cet écran montre, semaine par
 * semaine (la plus récente d'abord), les pages reçues puis ce que la lecture en
 * a tiré : **les blocages d'abord** — un exercice non fait, une correction
 * recopiée à côté d'une réponse fausse, une trace écrite inachevée —, ensuite
 * les compétences reconnues.
 *
 * Rien de ceci n'est montré à l'enfant, et rien n'entre dans `mastery` : une
 * page de cahier n'est pas une observation vérifiée (règle 3). Une
 * reconnaissance fausse se retire d'un geste ; la ligne est supprimée côté
 * serveur et la liste relue.
 *
 * Les vignettes sont servies sous URL signée à durée de vie courte (règle 8) ;
 * une page dont l'URL n'a pas pu être signée est dite « image indisponible »
 * plutôt que laissée en cadre vide, qui se lirait comme une page blanche.
 *
 * Monté comme troisième sous-onglet de « Ce qui a été vu » : les compétences
 * reconnues dans le cahier sont une preuve de plus de ce que la classe a traité.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { call } from "@/api/client";
import type {
  CahiersDeLaSemaineOut,
  MatiereDuCahierOut,
  ReconnaissanceDuCahierOut,
  SemaineDuCahierOut,
} from "@/api/schema";
import { enClairCourt } from "@/ui/dates";
import { useEnfants } from "@/ui/enfants";
import { etiquette } from "@/ui/matieres";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

/** L'état de la lecture d'une semaine, en clair. */
export function etatDeLaLecture(etat: string): string {
  switch (etat) {
    case "reading":
      return "en cours de lecture";
    case "done":
      return "lue";
    case "failed":
      return "lecture impossible — les pages restent visibles";
    default:
      // `pending` et `aucune` : rien n'a encore été lu.
      return "pas encore lue";
  }
}

/** Le signe d'un blocage, en clair. */
export function signeEnClair(signe: string | null | undefined): string {
  switch (signe) {
    case "exercice_non_fait":
      return "exercice non fait";
    case "correction_recopiee":
      return "correction recopiée à côté d'une réponse fausse";
    case "trace_inachevee":
      return "trace écrite inachevée";
    default:
      return "autre difficulté";
  }
}

/** « Semaine du lundi 28 septembre ». */
export function titreDeLaSemaine(semaine: string): string {
  return `Semaine du ${enClairCourt(semaine.slice(0, 10))}`;
}

type Etat =
  | { quoi: "chargement" }
  | { quoi: "panne" }
  | { quoi: "pret"; reponse: CahiersDeLaSemaineOut };

export function CahierDeLaSemaineScreen() {
  const { childId = "" } = useParams();
  const enfant = useEnfants().find((candidat) => candidat.child_id === childId);
  const [etat, setEtat] = useState<Etat>({ quoi: "chargement" });
  const [retrait, setRetrait] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      const reponse = await call("cahierDeLaSemaine", { params: { child_id: childId } });
      setEtat({ quoi: "pret", reponse });
    } catch {
      setEtat({ quoi: "panne" });
    }
  }, [childId]);

  useEffect(() => {
    setEtat({ quoi: "chargement" });
    void charger();
  }, [charger]);

  const retirer = async (reconnaissanceId: string) => {
    setRetrait(null);
    try {
      await call("retirerUneReconnaissanceDuCahier", {
        params: { child_id: childId, reconnaissance_id: reconnaissanceId },
      });
      await charger();
    } catch {
      setRetrait("La reconnaissance n'a pas pu être retirée. Réessayez.");
    }
  };

  const titre = enfant
    ? `Le cahier de la semaine — ${enfant.display_name}`
    : "Le cahier de la semaine";

  if (etat.quoi === "chargement") return <Loading label="Lecture du cahier de la semaine…" />;

  if (etat.quoi === "panne") {
    return (
      <Screen title={titre}>
        <ErrorNote>Le cahier de la semaine n'a pas pu être chargé.</ErrorNote>
        <Button variant="quiet" onClick={() => void charger()}>
          Réessayer
        </Button>
      </Screen>
    );
  }

  const semaines = etat.reponse.semaines ?? [];
  const triees = [...semaines].sort((a, b) => b.semaine.localeCompare(a.semaine));

  return (
    <Screen
      title={titre}
      lead="Les pages du cahier de classe que l'enfant a photographiées en fin de semaine, et ce que la lecture en a tiré."
    >
      {retrait ? <ErrorNote>{retrait}</ErrorNote> : null}
      {triees.length === 0 ? (
        <Empty>Aucune page de cahier reçue pour le moment.</Empty>
      ) : (
        triees.map((semaine) => (
          <Semaine key={semaine.semaine} semaine={semaine} retirer={retirer} />
        ))
      )}
    </Screen>
  );
}

function Semaine({
  semaine,
  retirer,
}: {
  semaine: SemaineDuCahierOut;
  retirer: (id: string) => Promise<void>;
}) {
  const matieres = semaine.matieres ?? [];
  const reconnaissances = semaine.reconnaissances ?? [];
  const blocages = reconnaissances.filter((r) => r.bloquee);
  const reconnues = reconnaissances.filter((r) => !r.bloquee);

  return (
    <Card title={titreDeLaSemaine(semaine.semaine)}>
      <p className="muted small">Lecture : {etatDeLaLecture(semaine.etat)}</p>

      {matieres.length === 0 ? (
        <p className="muted small">Aucune page reçue cette semaine.</p>
      ) : (
        matieres.map((matiere) => <Pages key={matiere.matiere} matiere={matiere} />)
      )}

      {blocages.length > 0 ? (
        <>
          <h3 className="small">Blocages</h3>
          <ul>
            {blocages.map((r) => (
              <Reconnaissance key={r.id} reconnaissance={r} retirer={retirer} />
            ))}
          </ul>
        </>
      ) : null}

      {reconnues.length > 0 ? (
        <>
          <h3 className="small">Compétences reconnues</h3>
          <ul>
            {reconnues.map((r) => (
              <Reconnaissance key={r.id} reconnaissance={r} retirer={retirer} />
            ))}
          </ul>
        </>
      ) : null}
    </Card>
  );
}

function Pages({ matiere }: { matiere: MatiereDuCahierOut }) {
  const pages = matiere.pages ?? [];
  return (
    <div>
      <p className="small">
        <strong>{matiere.libelle}</strong>
      </p>
      {pages.length === 0 ? (
        <p className="muted small">Aucune page.</p>
      ) : (
        <ul className="devoirs__photos">
          {pages.map((page, index) => (
            <li key={page.id}>
              {page.url ? (
                <a href={page.url} target="_blank" rel="noreferrer">
                  <img
                    className="devoirs__photo"
                    src={page.url}
                    alt={`Page ${index + 1} du cahier de ${matiere.libelle}`}
                    loading="lazy"
                  />
                </a>
              ) : (
                <span className="muted small">Image indisponible</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Reconnaissance({
  reconnaissance,
  retirer,
}: {
  reconnaissance: ReconnaissanceDuCahierOut;
  retirer: (id: string) => Promise<void>;
}) {
  const [enCours, setEnCours] = useState(false);
  return (
    <li data-reconnaissance={reconnaissance.id}>
      <p className="small">
        <strong>{reconnaissance.libelle}</strong>
        <span className="muted"> · {etiquette(reconnaissance.matiere)}</span>
        {reconnaissance.bloquee ? (
          <span> · {signeEnClair(reconnaissance.signe)}</span>
        ) : null}
        {reconnaissance.hors_ed ? (
          <span className="muted"> · absent du cahier de textes</span>
        ) : null}
      </p>
      {reconnaissance.justification ? (
        <p className="muted small">{reconnaissance.justification}</p>
      ) : null}
      <Button
        variant="quiet"
        disabled={enCours}
        onClick={() => {
          setEnCours(true);
          void retirer(reconnaissance.id).finally(() => setEnCours(false));
        }}
      >
        Retirer
      </Button>
    </li>
  );
}
